import { yupResolver } from '@hookform/resolvers/yup';
import { restaurantValidationSchema } from './restaurantValidation';
import { LanguageCodes } from '../../../app/data/models/common/localizedLanguage';
import { createRestaurantAsync } from './createRestaurantSlice';
import { updateRestaurantAsync } from './updateRestaurantSlice';
import { CreateRestaurantFormData } from '../../../app/data/models/restaurants/createRestaurantFormData';
import Restaurant from '../../../app/data/models/restaurant';
import { useAppSelector } from '../../../app/store/configureStore';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useAppDispatch } from '../../../app/store/configureStore';
import {
  Grid,
  TextField,
  Box,
  Chip,
  Typography,
  Autocomplete,
  Paper,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import FormButtons from '../../../app/components/FormButtons';
import { useEffect, useCallback } from 'react';
import AppDropzone from '../../../app/components/AppDropzone';
import OSMLocationPicker from '../../../app/components/OSMLocationPicker';
import { toast } from 'react-toastify';
import { LanguageCode } from '../../../app/data/models/common/localizedLanguage';
import ListTitleBox from '../../../app/components/list/ListTitleBox';
import {
  fetchRestaurantAsync,
  getRestaurantSelectors,
} from './restaurantSlice';

// Available languages configuration
const AVAILABLE_LANGUAGES: { code: LanguageCode; name: string }[] = [
  { code: LanguageCodes.en, name: 'English' },
  { code: LanguageCodes.de, name: 'Deutsch' },
  { code: LanguageCodes.it, name: 'Italiano' },
  { code: LanguageCodes.fr, name: 'Français' },
];

interface Props {
  restaurant?: Restaurant;
  cancelEdit: () => void;
}

export default function RestaurantForm({ restaurant, cancelEdit }: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const restaurantSelectors = getRestaurantSelectors();
  const selectedRestaurant = useAppSelector((state) =>
    restaurant
      ? restaurantSelectors.selectById(state, restaurant.id)
      : undefined
  );

  const { control, handleSubmit, setValue, watch } =
    useForm<CreateRestaurantFormData>({
      mode: 'onTouched',
      resolver: yupResolver(restaurantValidationSchema(t)),
    });

  const watchLogo = watch('logo') as File;
  const watchBanner = watch('banner') as File;

  const { user } = useAppSelector((state) => state.account);
  const isSuperAdmin = user?.roles?.isSuperAdmin || false;
  const { status: createStatus } = useAppSelector(
    (state) => state.createRestaurant
  );
  const { status: updateStatus } = useAppSelector(
    (state) => state.updateRestaurant
  );

  const loading =
    createStatus.includes('pending') || updateStatus.includes('pending');

  useEffect(() => {
    if (restaurant && restaurant.id) {
      dispatch(fetchRestaurantAsync(restaurant.id));
    }
  }, [restaurant, dispatch]);

  useEffect(() => {
    if (selectedRestaurant) {
      setValue('name', selectedRestaurant.name);
      setValue('location', selectedRestaurant.location || undefined);
      setValue('phoneNumber', selectedRestaurant.phoneNumber || undefined);
      setValue(
        'languageCodes',
        (selectedRestaurant.languageCodes || [LanguageCodes.en]).filter(
          Boolean
        ) as LanguageCodes[]
      );
    } else {
      // Default to English if creating a new restaurant
      setValue('languageCodes', [LanguageCodes.en]);
    }
  }, [selectedRestaurant, setValue]);

  const isDeepEqual = (obj1: unknown, obj2: unknown): boolean => {
    if (obj1 === obj2) return true;

    if (
      typeof obj1 !== 'object' ||
      obj1 === null ||
      typeof obj2 !== 'object' ||
      obj2 === null
    ) {
      return false;
    }

    const keys1 = Object.keys(obj1 as Record<string, unknown>);
    const keys2 = Object.keys(obj2 as Record<string, unknown>);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      if (
        !keys2.includes(key) ||
        !isDeepEqual(
          (obj1 as Record<string, unknown>)[key],
          (obj2 as Record<string, unknown>)[key]
        )
      ) {
        return false;
      }
    }

    return true;
  };

  const handleSubmitData = async (data: CreateRestaurantFormData) => {
    const cleanedData: Partial<CreateRestaurantFormData> = {
      ...data,
      languageCodes: data.languageCodes.filter(Boolean) as LanguageCodes[],
    };

    if (selectedRestaurant) {
      // If location hasn't changed, remove it from the payload
      if (isDeepEqual(data.location, selectedRestaurant.location)) {
        delete cleanedData.location;
      }
    }

    try {
      if (selectedRestaurant) {
        await dispatch(
          updateRestaurantAsync({
            id: selectedRestaurant.id,
            data: cleanedData as CreateRestaurantFormData,
          })
        ).unwrap();
        toast.success(t('restaurant.update.success'));
      } else {
        await dispatch(createRestaurantAsync(cleanedData)).unwrap();
        toast.success(t('restaurant.create.success'));
      }
      cancelEdit();
    } catch (error) {
      console.error('Error submitting restaurant:', error);
      toast.error(t('common.error'));
    }
  };

  const handleBack = useCallback(() => {
    cancelEdit();
  }, [cancelEdit]);

  const header = (
    <ListTitleBox
      title={
        selectedRestaurant
          ? t('restaurant.form.edit.title')
          : t('restaurant.form.create.title')
      }
      onBackClick={handleBack}
    />
  );

  return (
    <>
      {header}
      <Box component={Paper} sx={{ width: '100%', mb: 3, p: 2 }}>
        <form onSubmit={handleSubmit(handleSubmitData)} noValidate>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label={t('restaurant.form.fields.name')}
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="languageCodes"
                control={control}
                render={({ field }) => (
                  <Box>
                    <Autocomplete
                      multiple
                      id="languages-selector"
                      options={AVAILABLE_LANGUAGES}
                      getOptionLabel={(option) =>
                        t(`common.language.${option?.code}`)
                      }
                      disabled={!isSuperAdmin}
                      value={
                        field.value
                          ?.map((code) =>
                            AVAILABLE_LANGUAGES.find(
                              (lang) => lang.code === code
                            )
                          )
                          .filter(Boolean) || []
                      }
                      onChange={(_, newValue) => {
                        field.onChange(
                          newValue.map(
                            (item) =>
                              (item?.code as LanguageCodes) ?? LanguageCodes.en
                          )
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('restaurant.form.fields.languages.title')}
                          placeholder={t(
                            'restaurant.form.fields.languages.placeholder'
                          )}
                        />
                      )}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                          <Chip
                            label={
                              index === 0
                                ? `${t(`common.language.${option?.code}`)} (${t('restaurant.form.fields.languages.default')})`
                                : t(`common.language.${option?.code}`)
                            }
                            {...getTagProps({ index })}
                            color={index === 0 ? 'primary' : 'default'}
                          />
                        ))
                      }
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: 'block' }}
                    >
                      {t('restaurant.form.fields.languages.helper')}
                      {!isSuperAdmin && (
                        <span style={{ color: 'red', marginLeft: '8px' }}>
                          {t(
                            'common.contactCustomerCare',
                            'Please contact customer care to modify this field'
                          )}
                        </span>
                      )}
                    </Typography>
                  </Box>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="location"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <OSMLocationPicker
                    label={t('restaurant.form.fields.address')}
                    error={!!error}
                    helperText={error?.message}
                    value={field.value}
                    onChange={field.onChange}
                    initialAddress={selectedRestaurant?.location?.address}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('restaurant.form.fields.phone')}
                    fullWidth
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="logo"
                control={control}
                render={() => (
                  <>
                    <AppDropzone
                      control={control}
                      name="logo"
                      label={t('restaurant.form.fields.logo')}
                      maxWidth={512}
                      maxHeight={512}
                      aspectRatio={1}
                    />
                    {(watchLogo || selectedRestaurant?.logoUrl) && (
                      <Box mt={2}>
                        <img
                          src={
                            watchLogo
                              ? URL.createObjectURL(watchLogo)
                              : selectedRestaurant?.logoUrl
                          }
                          alt="Logo preview"
                          style={{
                            maxWidth: 200,
                            maxHeight: 200,
                            objectFit: 'contain',
                          }}
                        />
                      </Box>
                    )}
                  </>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="banner"
                control={control}
                render={() => (
                  <>
                    <AppDropzone
                      control={control}
                      name="banner"
                      label={t('restaurant.form.fields.banner')}
                      maxWidth={1920}
                      maxHeight={1080}
                      aspectRatio={16 / 9}
                    />
                    {(watchBanner || selectedRestaurant?.bannerUrl) && (
                      <Box mt={2}>
                        <img
                          src={
                            watchBanner
                              ? URL.createObjectURL(watchBanner)
                              : selectedRestaurant?.bannerUrl
                          }
                          alt="Banner preview"
                          style={{
                            maxWidth: '100%',
                            maxHeight: 200,
                            objectFit: 'cover',
                          }}
                        />
                      </Box>
                    )}
                  </>
                )}
              />
            </Grid>
          </Grid>

          <FormButtons
            cancelEdit={cancelEdit}
            loading={loading}
            submitText={
              selectedRestaurant ? t('common.update') : t('common.create')
            }
          />
        </form>
      </Box>
    </>
  );
}
